import React, { useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import ReactionsModal from "./ReactionsModal";
import { defaultPfpUrl } from "../../utils";
import { cn } from "../../../lib/utils";

/**
 * Single reaction chip per message.
 * One reactor → emoji + pfp; multiple → emojis side by side (+ total count).
 * @param {Object} props
 * @param {Array<Object>} props.reactions
 * @param {number} props.userId
 * @param {boolean} [props.isOwnMessage]
 * @param {(reactionId: number) => void|Promise<void>} [props.onRemoveOwnReaction]
 * @returns {JSX.Element | null}
 */
function MessageReactions({
  reactions,
  userId,
  isOwnMessage = false,
  onRemoveOwnReaction,
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const totalCount = useMemo(
    () =>
      (reactions || []).reduce((sum, group) => sum + (group.count || 0), 0),
    [reactions]
  );

  if (!reactions || reactions.length === 0 || totalCount === 0) {
    return null;
  }

  const isSingle = totalCount === 1;
  const singleGroup = isSingle ? reactions[0] : null;
  const singleUser = singleGroup?.users?.[0] || null;

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className={cn(
          // Cutout border: always match ChatFrame / MessageList bg (--color-bg-primary).
          "absolute -bottom-2.5 z-10 inline-flex h-5 max-w-full items-center gap-0.5 rounded-full border-2 border-[color:var(--color-bg-primary)] bg-ui-card px-1 text-xs transition-colors hover:bg-ui-muted",
          isOwnMessage ? "right-0" : "left-0"
        )}
        aria-label="View reactions"
      >
        {isSingle && singleGroup ? (
          <>
            <span className="leading-none text-[11px]">{singleGroup.emoji}</span>
            {singleUser ? (
              <Avatar className="h-3.5 w-3.5 shrink-0">
                <AvatarImage
                  src={singleUser.user_pfp || defaultPfpUrl}
                  alt={singleUser.user_name || "Reactor"}
                  onError={(e) => {
                    e.target.src = defaultPfpUrl;
                  }}
                />
                <AvatarFallback className="text-[7px]">
                  {(singleUser.user_name || "?").slice(0, 1).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            ) : null}
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-px leading-none text-[11px]">
              {reactions.map((group) => (
                <span key={group.emoji}>{group.emoji}</span>
              ))}
            </span>
            <span className="pr-px text-[10px] text-text-primary">{totalCount}</span>
          </>
        )}
      </button>
      <ReactionsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        reactions={reactions}
        userId={userId}
        onRemoveOwn={async (reactionId) => {
          if (typeof onRemoveOwnReaction === "function") {
            await onRemoveOwnReaction(reactionId);
          }
          setModalOpen(false);
        }}
      />
    </>
  );
}

export default MessageReactions;
